import { useSyncExternalStore } from "react";
import { Link } from "react-router-dom";
import { jobsViewedStore } from "../jobs/jobsViewedStore";
import { useJobsList } from "../jobs/useJobs";

// PRD-001 §Job-history-and-lifecycle: badge surfaces jobs that transitioned
// to a terminal state (`completed`, `failed`, `cancelled`) since the user
// last viewed history. `expired` is intentionally excluded — it's a
// much-later automated state flip (~7 days post-completion) and is not
// something a CSM needs to be nudged about; the auto-removal column on the
// history page is the right surface for that.
const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled"]);

/**
 * Header badge counting jobs that have transitioned to a terminal state
 * since the user last opened the history page. Clicking it navigates to
 * `/jobs`, which on mount calls `jobsViewedStore.markViewedNow()` — which
 * collapses the count to zero on the next render.
 */
export function JobsBadge() {
    const lastViewedAt = useSyncExternalStore(
        jobsViewedStore.subscribe,
        jobsViewedStore.getLastViewedAt,
        jobsViewedStore.getLastViewedAt,
    );
    const { jobs } = useJobsList();

    const count = jobs.filter((job) => {
        // PRD-001 rev 13: awaiting-confirmation is precisely the "user must
        // come look" state the badge exists for — a paused job waits on a
        // human Continue and auto-cancels after 7 days if never seen.
        if (job.status === "awaiting_confirmation") {
            if (!job.awaitingConfirmationAt) return false;
            const at = Date.parse(job.awaitingConfirmationAt);
            return !Number.isNaN(at) && at > lastViewedAt;
        }
        if (!TERMINAL_STATUSES.has(job.status)) return false;
        // Worker writes `completedAt` on every terminal flip
        // (succeeded / failed / cancelled).
        if (!job.completedAt) return false;
        const t = Date.parse(job.completedAt);
        if (Number.isNaN(t)) return false;
        return t > lastViewedAt;
    }).length;

    return (
        <Link
            to="/jobs"
            className="jobs-badge"
            data-testid="jobs-badge"
            aria-label={count > 0 ? `${count} new job updates` : "Job history"}
        >
            <i className="fa-solid fa-clock-rotate-left" aria-hidden="true" />
            <span>Jobs</span>
            {count > 0 && (
                <span className="badge-count" data-testid="jobs-badge-count">
                    {count}
                </span>
            )}
        </Link>
    );
}
